/**
 * Compact Schemas — Lightweight tool descriptions for extended (non-core) services.
 * These are injected into the system prompt instead of full OpenAI function schemas,
 * reducing token usage by ~89% per tool.
 *
 * Convention:
 *   tool_name(param:type!, param:type=default) — Description
 *   ! = required, no ! = optional, =val = default value
 *
 * The LLM calls these via the generic `execute_mcp_tool` function.
 * Full schemas are kept locally in toolCatalog for validation.
 */

// ─── Compact Schema Blocks by Service ─────────────────────────────────────────

const COMPACT_SCHEMAS: Record<string, string> = {
  gmail: `GMAIL:
  gmail_list_messages(query:str, max_results:int=10, label_ids:str[]) — Buscar mensajes
  gmail_get_message(message_id:str!) — Obtener mensaje por ID
  gmail_send_message(to:str!, subject:str!, body:str!, cc:str, bcc:str) — Enviar email
  gmail_delete_message(message_id:str!) — Eliminar mensaje
  gmail_list_labels() — Listar labels del inbox
  gmail_create_label(name:str!, color:str) — Crear label
  gmail_list_drafts(max_results:int=10) — Listar borradores
  gmail_create_draft(to:str!, subject:str!, body:str!) — Crear borrador
  gmail_send_draft(draft_id:str!) — Enviar borrador
  gmail_delete_draft(draft_id:str!) — Eliminar borrador
  gmail_get_thread(thread_id:str!) — Obtener thread completo
  gmail_list_threads(query:str, max_results:int=10) — Listar threads
  gmail_trash_message(message_id:str!) — Mover a papelera
  gmail_untrash_message(message_id:str!) — Sacar de papelera
  gmail_modify_labels(message_id:str!, add:str[], remove:str[]) — Modificar labels
  gmail_get_attachment(message_id:str!, attachment_id:str!) — Descargar adjunto
  gmail_reply_message(message_id:str!, body:str!) — Responder mensaje
  gmail_forward_message(message_id:str!, to:str!) — Reenviar mensaje
  gmail_mark_read(message_id:str!) — Marcar como leído
  gmail_mark_unread(message_id:str!) — Marcar como no leído
  gmail_star_message(message_id:str!) — Destacar mensaje
  gmail_unstar_message(message_id:str!) — Quitar destacado
  gmail_get_profile() — Obtener perfil del usuario
  gmail_batch_modify(message_ids:str[]!, add:str[], remove:str[]) — Modificar múltiples`,
  calendar: `CALENDAR:
  calendar_list_calendars() — Listar calendarios
  calendar_list_events(calendar_id:str="primary", max_results:int=10, time_min:str, time_max:str, query:str) — Listar eventos
  calendar_get_event(calendar_id:str="primary", event_id:str!) — Obtener evento
  calendar_create_event(summary:str!, start_time:str!, end_time:str!, description:str, location:str, attendees:str[], calendar_id:str="primary") — Crear evento
  calendar_update_event(event_id:str!, summary:str, start_time:str, end_time:str, description:str, calendar_id:str="primary") — Actualizar evento
  calendar_delete_event(event_id:str!, calendar_id:str="primary") — Eliminar evento
  calendar_quick_add(text:str!, calendar_id:str="primary") — Crear evento con texto natural
  calendar_list_instances(event_id:str!, calendar_id:str="primary") — Instancias de evento recurrente
  calendar_move_event(event_id:str!, destination_calendar:str!) — Mover evento entre calendarios
  calendar_get_colors() — Obtener colores disponibles
  calendar_get_settings() — Obtener configuración
  calendar_watch_events(calendar_id:str="primary", webhook_url:str!) — Watch para cambios`,
  drive: `DRIVE:
  drive_list_files(query:str, max_results:int=10, order_by:str) — Listar archivos
  drive_get_file(file_id:str!) — Obtener metadata
  drive_create_file(name:str!, mime_type:str, content:str, parent_id:str) — Crear archivo
  drive_upload_file(name:str!, content:str!, mime_type:str!, parent_id:str) — Subir archivo
  drive_update_file(file_id:str!, name:str, content:str) — Actualizar archivo
  drive_delete_file(file_id:str!) — Eliminar archivo
  drive_create_folder(name:str!, parent_id:str) — Crear carpeta
  drive_share_file(file_id:str!, email:str!, role:str="reader") — Compartir archivo
  drive_copy_file(file_id:str!, name:str) — Copiar archivo
  drive_move_file(file_id:str!, new_parent:str!) — Mover archivo
  drive_get_permissions(file_id:str!) — Ver permisos
  drive_delete_permission(file_id:str!, permission_id:str!) — Quitar permiso
  drive_export_file(file_id:str!, mime_type:str!) — Exportar (Google→PDF/etc)
  drive_download_file(file_id:str!) — Descargar contenido
  drive_search_files(query:str!, max_results:int=10) — Búsqueda avanzada
  drive_get_storage_quota() — Ver espacio usado
  drive_list_shared(max_results:int=10) — Archivos compartidos conmigo
  drive_trash_file(file_id:str!) — Mover a papelera`,
  docs: `DOCS:
  docs_create_document(title:str!) — Crear documento
  docs_get_document(document_id:str!) — Leer documento
  docs_insert_text(document_id:str!, text:str!, index:int=1) — Insertar texto
  docs_delete_text(document_id:str!, start_index:int!, end_index:int!) — Eliminar texto
  docs_replace_text(document_id:str!, find:str!, replace:str!) — Buscar y reemplazar
  docs_batch_update(document_id:str!, requests:any[]!) — Operaciones batch`,
  sheets: `SHEETS:
  sheets_create_spreadsheet(title:str!) — Crear spreadsheet
  sheets_get_values(spreadsheet_id:str!, range:str!) — Leer celdas
  sheets_update_values(spreadsheet_id:str!, range:str!, values:any[]!) — Escribir celdas
  sheets_append_values(spreadsheet_id:str!, range:str!, values:any[]!) — Agregar filas
  sheets_create_sheet(spreadsheet_id:str!, title:str!) — Agregar hoja
  sheets_delete_sheet(spreadsheet_id:str!, sheet_id:int!) — Eliminar hoja
  sheets_get_spreadsheet(spreadsheet_id:str!) — Metadata del spreadsheet
  sheets_clear_values(spreadsheet_id:str!, range:str!) — Limpiar celdas
  sheets_batch_update(spreadsheet_id:str!, requests:any[]!) — Operaciones batch`,
  forms: `FORMS:
  forms_create_form(title:str!) — Crear formulario
  forms_get_form(form_id:str!) — Obtener estructura
  forms_list_responses(form_id:str!, max_results:int=50) — Listar respuestas
  forms_get_response(form_id:str!, response_id:str!) — Obtener respuesta
  forms_batch_update(form_id:str!, requests:any[]!) — Actualizar formulario
  forms_watch_form(form_id:str!, webhook_url:str!) — Watch para respuestas`,
  tasks: `TASKS:
  tasks_list_tasklists() — Listar listas de tareas
  tasks_create_tasklist(title:str!) — Crear lista
  tasks_get_tasklist(tasklist_id:str!) — Obtener lista
  tasks_update_tasklist(tasklist_id:str!, title:str!) — Actualizar lista
  tasks_delete_tasklist(tasklist_id:str!) — Eliminar lista
  tasks_list_tasks(tasklist_id:str!, max_results:int=20, show_completed:bool=false) — Listar tareas
  tasks_create_task(tasklist_id:str!, title:str!, notes:str, due:str) — Crear tarea
  tasks_get_task(tasklist_id:str!, task_id:str!) — Obtener tarea
  tasks_update_task(tasklist_id:str!, task_id:str!, title:str, notes:str, status:str) — Actualizar tarea
  tasks_delete_task(tasklist_id:str!, task_id:str!) — Eliminar tarea
  tasks_complete_task(tasklist_id:str!, task_id:str!) — Completar tarea
  tasks_move_task(tasklist_id:str!, task_id:str!, parent:str, previous:str) — Reordenar tarea`,
  contacts: `CONTACTS:
  contacts_list_contacts(max_results:int=20, query:str) — Listar contactos
  contacts_get_contact(resource_name:str!) — Obtener contacto
  contacts_create_contact(given_name:str!, family_name:str, email:str, phone:str) — Crear contacto
  contacts_update_contact(resource_name:str!, given_name:str, family_name:str, email:str) — Actualizar
  contacts_delete_contact(resource_name:str!) — Eliminar contacto
  contacts_search_contacts(query:str!) — Buscar contactos
  contacts_list_groups() — Listar grupos
  contacts_create_group(name:str!) — Crear grupo
  contacts_get_group(resource_name:str!) — Obtener grupo
  contacts_delete_group(resource_name:str!) — Eliminar grupo`,
  chat: `CHAT:
  chat_list_spaces() — Listar espacios de chat
  chat_create_space(display_name:str!, type:str="GROUP") — Crear espacio
  chat_get_space(space_name:str!) — Obtener espacio
  chat_list_messages(space_name:str!, max_results:int=20) — Listar mensajes
  chat_create_message(space_name:str!, text:str!) — Enviar mensaje
  chat_get_message(message_name:str!) — Obtener mensaje
  chat_update_message(message_name:str!, text:str!) — Actualizar mensaje
  chat_delete_message(message_name:str!) — Eliminar mensaje
  chat_list_members(space_name:str!) — Listar miembros
  chat_create_member(space_name:str!, member:str!) — Agregar miembro
  chat_delete_member(member_name:str!) — Quitar miembro
  chat_upload_attachment(space_name:str!, filename:str!, content:str!) — Subir adjunto`,
  slack: `SLACK:
  slack_get_channels() — Listar canales del workspace
  slack_read_channel(channel_id:str!, limit:int=30) — Leer mensajes de un canal
  slack_send_message(channel_id:str!, text:str!) — Enviar mensaje a un canal
  slack_get_users() — Listar usuarios del workspace
  slack_get_channel_by_name(channel_name:str!) — Buscar canal por nombre para obtener su ID`,
  notion: `NOTION:
  notion_search(query:str, filter:str) — Buscar páginas y bases de datos
  notion_list_databases() — Listar bases de datos del workspace
  notion_list_pages() — Listar páginas del workspace
  notion_get_page(page_id:str!) — Obtener propiedades de una página
  notion_create_page(parent:obj!, properties:obj!) — Crear página
  notion_update_page(page_id:str!, properties:obj!) — Actualizar página
  notion_archive_page(page_id:str!) — Archivar página
  notion_get_database(database_id:str!) — Obtener esquema de base de datos
  notion_query_database(database_id:str!, filter:obj, sorts:arr) — Consultar base de datos con filtros
  notion_get_block_children(block_id:str!) — Obtener bloques hijos
  notion_append_blocks(block_id:str!, children:arr!) — Agregar bloques de contenido
  notion_health_check(echo:str) — Verificar conectividad`,
}

// ─── Core services that always use full schemas ───────────────────────────────

export const CORE_SERVICES = ['jira', 'confluence']

// ─── Extended services that use compact schemas ───────────────────────────────

export const EXTENDED_SERVICES = [
  'gmail',
  'calendar',
  'drive',
  'docs',
  'sheets',
  'forms',
  'tasks',
  'contacts',
  'chat',
  'slack',
  'notion',
]

// ─── Service to MCP mapping ──────────────────────────────────────────────────

/** Maps classifier service IDs to the service type used in toolCatalog */
export const SERVICE_TO_CATALOG: Record<string, string> = {
  jira: 'jira',
  confluence: 'confluence',
  slack: 'slack',
  gmail: 'google-mcp',
  calendar: 'google-mcp',
  drive: 'google-mcp',
  docs: 'google-mcp',
  sheets: 'google-mcp',
  forms: 'google-mcp',
  tasks: 'google-mcp',
  contacts: 'google-mcp',
  chat: 'google-mcp',
  // Legacy google tools (create doc/sheet/slide via Firebase)
  google: 'google',
  // Notion MCP
  notion: 'notion',
}

// ─── Build compact schema prompt block ────────────────────────────────────────

/**
 * Build the compact schema section for the system prompt.
 * Only includes schemas for the given extended services.
 */
export function buildCompactSchemaBlock(services: string[]): string {
  const extendedServices = services.filter((s) => EXTENDED_SERVICES.includes(s))
  if (extendedServices.length === 0) return ''
  const blocks = extendedServices.map((s) => COMPACT_SCHEMAS[s]).filter(Boolean)
  if (blocks.length === 0) return ''
  return `
═══════════════════════════════════════════════════
🔌 HERRAMIENTAS EXTENDIDAS (usar con execute_mcp_tool)
═══════════════════════════════════════════════════

Para ejecutar estas herramientas, usá la tool "execute_mcp_tool" con:
- tool_name: nombre exacto de la herramienta
- service: servicio al que pertenece (gmail, calendar, drive, docs, sheets, forms, tasks, contacts, chat, slack)
- params: objeto con los parámetros

Convención de parámetros:
- param:str! = string requerido
- param:str = string opcional
- param:int=10 = número opcional con default 10
- param:str[] = array de strings opcional
- param:bool=false = boolean opcional

${blocks.join('\n\n')}`
}

// ─── execute_mcp_tool schema (OpenAI function format) ─────────────────────────

/**
 * The generic tool schema that the LLM uses to call any extended service tool.
 * This single schema replaces 100+ individual schemas.
 */
export const EXECUTE_MCP_TOOL_SCHEMA = {
  type: 'function' as const,
  function: {
    name: 'execute_mcp_tool',
    description:
      'Ejecuta una herramienta de un servicio extendido (Gmail, Calendar, Drive, Docs, Sheets, Forms, Tasks, Contacts, Chat, Slack). Consultá la lista de herramientas extendidas en el prompt para ver los nombres y parámetros disponibles.',
    parameters: {
      type: 'object',
      properties: {
        tool_name: {
          type: 'string',
          description:
            'Nombre exacto de la herramienta a ejecutar (ej: gmail_list_messages, calendar_create_event)',
        },
        service: {
          type: 'string',
          description: 'Servicio al que pertenece la herramienta',
          enum: [
            'gmail',
            'calendar',
            'drive',
            'docs',
            'sheets',
            'forms',
            'tasks',
            'contacts',
            'chat',
            'slack',
          ],
        },
        params: {
          type: 'object',
          description:
            'Parámetros de la herramienta según la documentación en el prompt',
        },
      },
      required: ['tool_name', 'service', 'params'],
    },
  },
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export { COMPACT_SCHEMAS }
